import { useMemo, useState, useEffect } from 'react';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { recommendationsService } from '../../services/recommendations';

function startOfDay(d: Date) {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export default function AnalyticsPage() {
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(new Date()));
  const { selectedRestaurantId } = useSelectedRestaurant();
  type ParsedTip = {
    id: string;
    date: Date;
    title: string;
    description: string;
    impact: string;
    priority: 'alto' | 'medio' | 'bajo' | 'desconocido';
    why?: string;
    actions?: string[];
  };
  const [serverTips, setServerTips] = useState<Array<ParsedTip>>([]);

  // (Métricas simuladas removidas; se muestran solo recomendaciones filtradas por día)

  // Cargar recomendaciones desde backend
  useEffect(() => {
    const load = async () => {
      if (!selectedRestaurantId) return;
      // Pedir recomendaciones del día seleccionado
      const yyyy = selectedDate.getFullYear();
      const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const dd = String(selectedDate.getDate()).padStart(2, '0');
      const dateParam = `${yyyy}-${mm}-${dd}`;
      const record = await recommendationsService.getByRestaurant(selectedRestaurantId, { date: dateParam });
      if (!record) {
        setServerTips([]);
        return;
      }
      // recommendations es jsonb: idealmente un array [{ window, text }]
      const createdAt = record.createdAt ? new Date(record.createdAt) : new Date();
      const tips: Array<ParsedTip> = [];
      try {
        const rec = record.recommendations;
        if (Array.isArray(rec) && rec.length > 0) {
          const first = rec[0];
          const rawText: string = String(first?.text || '');
          // Parse básico: separar por líneas numeradas "1) ...", tomar primeras 3
          const lines = rawText.split('\n').map(s => s.trim()).filter(Boolean);
          const items = lines.filter(l => /^\d+\)/.test(l)).slice(0, 5);
          if (items.length > 0) {
            items.forEach((l, idx) => {
              const content = l.replace(/^\d+\)\s*/, '');
              // Extraer prioridad, por qué, acciones
              const lower = content.toLowerCase();
              const prioMatch = lower.match(/prioridad:\s*(alto|medio|bajo)/);
              const priority = (prioMatch?.[1] as 'alto' | 'medio' | 'bajo' | undefined) || 'desconocido';
              const whyMatch = content.match(/por qué:\s*([^\.]+(?:\.[^a].*)?)/i);
              const actionsMatch = content.match(/acciones:\s*(.+?)(?:\.|\s*evidencia:|$)/i);
              const actions = actionsMatch ? actionsMatch[1].split(';').map(s => s.trim()).filter(Boolean) : [];
              tips.push({
                id: `srv-${idx}`,
                date: createdAt,
                title: content.split('—')[0]?.trim() || `Recomendación #${idx + 1}`,
                description: content,
                impact: 'operación',
                priority,
                why: whyMatch ? whyMatch[1].trim() : undefined,
                actions,
              });
            });
          } else {
            tips.push({
              id: 'srv-0',
              date: createdAt,
              title: 'Recomendaciones generadas',
              description: rawText.slice(0, 240),
              impact: 'operación',
              priority: 'desconocido',
            });
          }
        }
      } catch {
        // fallback básico
      }
      setServerTips(tips);
    };
    load();
  }, [selectedRestaurantId, selectedDate]);

  // Utilidades para semana actual
  const weekDays = useMemo(() => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Dom, 1 = Lun, ... 6 = Sáb
    const mondayOffset = (dayOfWeek + 6) % 7; // convierte Lunes en 0
    const monday = new Date(today);
    monday.setDate(today.getDate() - mondayOffset);
    monday.setHours(0, 0, 0, 0);
    const days: { date: Date; label: string }[] = [];
    const labels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    for (let i = 0; i < 7; i++) {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      days.push({ date: d, label: labels[i] });
    }
    return days;
  }, []);

  const filteredTips = useMemo(() => {
    const sel = startOfDay(selectedDate);
    const list = serverTips;
    return list.filter(t => startOfDay(t.date).getTime() === sel.getTime());
  }, [serverTips, selectedDate]);

  // Helpers de estilos
  const priorityBadge = (priority: ParsedTip['priority']) => {
    switch (priority) {
      case 'alto':
        return 'bg-red-100 border-red-200 text-red-700';
      case 'medio':
        return 'bg-yellow-100 border-yellow-200 text-yellow-700';
      case 'bajo':
        return 'bg-green-100 border-green-200 text-green-700';
      default:
        return 'bg-gray-100 border-gray-200 text-gray-700';
    }
  };
  const priorityStripe = (priority: ParsedTip['priority']) => {
    switch (priority) {
      case 'alto':
        return 'border-l-4 border-red-500';
      case 'medio':
        return 'border-l-4 border-yellow-500';
      case 'bajo':
        return 'border-l-4 border-green-500';
      default:
        return 'border-l-4 border-gray-300';
    }
  };
  

  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-4 lg:px-8 py-4 space-y-4">
      {/* Filtro de periodo */}
      <div className="card p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          {/* Selector de día de la semana actual para recomendaciones */}
          <div className="flex items-center gap-2">
            {weekDays.map(({ date, label }) => {
              const isSelected = startOfDay(date).getTime() === startOfDay(selectedDate).getTime();
              const isFuture =
                startOfDay(date).getTime() > startOfDay(new Date()).getTime();
              return (
                <button
                  key={date.toISOString()}
                  className={`px-2.5 py-1.5 rounded-lg text-sm border ${
                    isSelected
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : isFuture
                        ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    if (!isFuture) setSelectedDate(date);
                  }}
                  disabled={isFuture}
                  title={date.toLocaleDateString('es-MX')}
                >
                  {label} {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Consejos de IA (historial básico) */}
      <div className="card p-3 sm:p-4">
        <h3 className="text-base font-semibold mb-3">Recomendaciones de IA</h3>
        <div className="space-y-3">
          {filteredTips.map((tip) => {
            return (
              <div key={tip.id} className={`p-3 rounded-lg border border-gray-200 bg-white ${priorityStripe(tip.priority)}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-lg">🤖</div>
                      <div className="text-sm font-semibold text-gray-800">{tip.title}</div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className={`badge-modern ${priorityBadge(tip.priority)}`}>
                        Prioridad: {tip.priority !== 'desconocido' ? tip.priority : '—'}
                      </span>
                      <span className="badge-modern bg-blue-50 border-blue-200 text-blue-700">{tip.impact}</span>
                      <span className="text-[11px] text-gray-500">
                        {tip.date.toLocaleDateString('es-MX')} {tip.date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {tip.why && (
                      <div className="mt-2 text-xs text-gray-700">
                        <span className="font-medium text-gray-800">Por qué: </span>{tip.why}
                      </div>
                    )}
                    {tip.actions && tip.actions.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[12px] font-medium text-gray-800 mb-1">Acciones sugeridas</div>
                        <ul className="list-disc pl-5 text-xs text-gray-700 space-y-0.5">
                          {tip.actions.map((a, i) => (
                            <li key={i}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!tip.why && (!tip.actions || tip.actions.length === 0) && (
                      <div className="mt-2 text-xs text-gray-600">{tip.description}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {filteredTips.length === 0 && (
            <div className="text-center text-sm text-gray-500 py-6">
              Sin recomendaciones para {selectedDate.toLocaleDateString('es-MX')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}